import { useCallback, useState } from 'react';

import { stripFilesRecursive } from '../utils/stripFiles';
import { FormType } from './keys';

export interface UseSessionFormResult {
  sessionValues: FormType;
  handleFormValueChange: (nextValues: FormType) => void;
  setFormValues: (nextValues: FormType) => void;
  resetSessionForm: () => void;
  refreshSessionValues: () => void;
}

// Strip File objects from form data to prevent sessionStorage quota exceeded errors
// Files are replaced with metadata to indicate their presence
const stripFilesFromFormData = (data: FormType): FormType => {
  return stripFilesRecursive(data) as FormType;
};

export function useSessionForm(storageKey: string, defaultValues: FormType): UseSessionFormResult {
  const [sessionValues, setSessionValues] = useState<FormType>(() => {
    const saved = sessionStorage.getItem(storageKey);
    return saved ? (JSON.parse(saved) as FormType) : defaultValues;
  });

  // Use this during typing - only persists to sessionStorage, no re-render
  const handleFormValueChange = useCallback(
    (nextValues: FormType) => {
      try {
        const dataWithoutFiles = stripFilesFromFormData(nextValues);
        const jsonString = JSON.stringify(dataWithoutFiles);
        sessionStorage.setItem(storageKey, jsonString);
      } catch (error) {
        // If storage quota is exceeded, silently fail but keep state in memory
        // Form data remains in component state for the current session
      }
    },
    [storageKey],
  );

  // Use this for programmatic updates (e.g., inserting Titan data) - updates both storage and state
  const setFormValues = useCallback(
    (nextValues: FormType) => {
      try {
        const dataWithoutFiles = stripFilesFromFormData(nextValues);
        const jsonString = JSON.stringify(dataWithoutFiles);
        sessionStorage.setItem(storageKey, jsonString);
      } catch (error) {
        // If storage quota is exceeded, silently fail but keep state in memory
        // Form data remains in component state for the current session
      }
      setSessionValues(nextValues);
    },
    [storageKey],
  );

  // Call this when modal opens to sync state with sessionStorage
  const refreshSessionValues = useCallback(() => {
    const saved = sessionStorage.getItem(storageKey);
    const freshValues = saved ? (JSON.parse(saved) as FormType) : defaultValues;
    setSessionValues(freshValues);
  }, [storageKey, defaultValues]);

  const resetSessionForm = useCallback(() => {
    sessionStorage.removeItem(storageKey);
    setSessionValues(defaultValues);
  }, [storageKey, defaultValues]);

  return { sessionValues, handleFormValueChange, setFormValues, resetSessionForm, refreshSessionValues };
}
